import React from "react";
import { X, Download, ExternalLink } from "lucide-react";
import { Dialog, DialogContent } from "../ui/dialog";
import AudioPlayer from "./AudioPlayer";
import { downloadFile } from "../../lib/downloadFile";

/**
 * PerkViewerModal — in-app player modal for perks containing video or audio.
 * For video we use the native HTML5 player (preserves R2 streaming + scrub bar).
 * For audio we reuse the studio AudioPlayer (wavesurfer-backed).
 * Link-only perks (externalUrl with no media files) render their thumbnail
 * here and surface an "Open Resource" CTA that opens the URL in a new tab —
 * the modal acts as the description + handoff step.
 * Any additional non-video/non-audio attachments are listed underneath as
 * download chips so users can grab the project files alongside the demo.
 */
export default function PerkViewerModal({ perk, category, open, onOpenChange }) {
  if (!perk) return null;
  const attachments = perk.attachments || [];
  const video = attachments.find((a) => a.kind === "video");
  const audio = attachments.find((a) => a.kind === "audio");
  const extras = attachments.filter(
    (a) => a !== video && a !== audio && a.kind !== "image",
  );
  const externalUrl = (perk.externalUrl || "").trim();
  const linkOnly = !video && !audio && externalUrl;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="perk-viewer-modal"
        className="max-w-3xl w-[95vw] p-0 bg-[#0b0f1e] border-white/10 text-white overflow-hidden"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          data-testid="perk-viewer-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur flex items-center justify-center text-white/80 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Player / thumbnail */}
        {video ? (
          <video
            data-testid="perk-viewer-video"
            src={video.url}
            controls
            autoPlay
            className="w-full max-h-[65vh] bg-black"
          />
        ) : audio ? (
          <div className="p-6 pt-12 bg-[#10162a]">
            <AudioPlayer src={audio.url} title={audio.name || perk.title} testId="perk-viewer-audio" />
          </div>
        ) : linkOnly && perk.thumbnailUrl ? (
          <div
            data-testid="perk-viewer-thumbnail"
            className="aspect-[16/9] w-full overflow-hidden bg-[#0b0f1e]"
          >
            <img
              src={perk.thumbnailUrl}
              alt={perk.title}
              className="w-full h-full object-cover"
            />
          </div>
        ) : linkOnly ? (
          // Link-only perk with no thumbnail — show a sleek branded gradient
          // panel so the modal doesn't look empty.
          <div className="aspect-[16/7] w-full bg-gradient-to-br from-[#161a2c] via-[#10162a] to-[#0b0f1e] flex items-center justify-center">
            <ExternalLink className="w-14 h-14 text-paul-yellow/40" strokeWidth={1.5} />
          </div>
        ) : (
          <div className="aspect-[16/9] flex items-center justify-center text-white/40 text-sm">
            No video or audio attached.
          </div>
        )}

        {/* Meta */}
        <div className="px-5 md:px-7 py-5">
          {category && (
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-paul-yellow mb-2">
              {category.name}
            </div>
          )}
          <h2
            data-testid="perk-viewer-title"
            className="text-xl md:text-2xl font-extrabold text-white mb-2"
          >
            {perk.title}
          </h2>
          {perk.description && (
            <p
              data-testid="perk-viewer-description"
              className="text-sm md:text-base text-white/70 leading-relaxed whitespace-pre-wrap break-words"
            >
              {perk.description}
            </p>
          )}

          {externalUrl && (
            <div className="mt-5">
              <a
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="perk-viewer-open-resource"
                className="inline-flex items-center gap-2 bg-paul-yellow hover:brightness-105 text-navy font-bold text-sm md:text-base px-5 py-2.5 rounded-lg transition-all"
                style={{ color: "#0b1535" }}
              >
                <ExternalLink className="w-4 h-4" />
                Open Resource
              </a>
              <p className="text-[11px] text-white/35 mt-2 break-all">
                Opens {externalUrl} in a new tab
              </p>
            </div>
          )}

          {extras.length > 0 && (
            <div className="mt-5 pt-5 border-t border-white/10">
              <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">
                Also included
              </div>
              <div className="flex flex-wrap gap-2">
                {extras.map((a) => (
                  <button
                    key={a.url}
                    type="button"
                    data-testid={`perk-viewer-extra-${a.kind}`}
                    onClick={() => downloadFile(a.url, a.name || perk.title)}
                    className="inline-flex items-center gap-2 bg-[#10162a] hover:bg-paul-yellow/15 hover:text-paul-yellow border border-white/10 text-white/80 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    {a.name || a.kind}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
